// src/Pages/Dashboard/widgets/ProjectView/Tabs/Reports/LaborReportPreview.js

import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axiosWithAuth from "../../../../../../Redux/UtilsRedux/axiosWithAuth";
import html2pdf from "html2pdf.js";
import { API_ROUTES } from "../../../../../../Redux/Constants/apiRoutes";
import "../../../../../../Stylesheets/Dashboard/Tabs/Reports/ExpenseReportPrint.css";
import site360Logo from "../../../../../../assets/images/logo.png";
import { FaChevronLeft } from "react-icons/fa";

// Make sure this path is correct; it should mirror Material/Expense modal paths
import LaborReportModal from "../Labor/Component/LaborReportModal";

const formatCurrency = (val) => {
  if (val == null || Number.isNaN(Number(val))) return "₹0";
  return (
    "₹" + Number(val).toLocaleString("en-IN", { maximumFractionDigits: 2 })
  );
};

const formatDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const formatDateTime = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const LaborReportPreview = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  const qs = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const queryParams = useMemo(
    () => ({
      type: qs.get("type") || "labor",
      projectId: qs.get("projectId"),
      from: qs.get("from"),
      to: qs.get("to"),
      detail: qs.get("detail") || "summary",
      category: qs.get("category") || "all",
    }),
    [qs]
  );

  // -------- fetch JSON for preview --------
  useEffect(() => {
    const fetchReport = async () => {
      try {
        if (!queryParams.projectId || !queryParams.from || !queryParams.to) {
          setError("Missing projectId / from / to");
          setLoading(false);
          return;
        }

        const params = new URLSearchParams({
          type: "labor",
          projectId: queryParams.projectId,
          from: queryParams.from,
          to: queryParams.to,
          detail: queryParams.detail,
          category: queryParams.category,
          format: "json",
        }).toString();

        const url = `${API_ROUTES.GENERATE_REPORT}?${params}`;
        const res = await axiosWithAuth.get(url);

        setReport(res.data.data);
        setError(null);
      } catch (err) {
        console.error("Labor report fetch failed", err);
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load labor report data";
        setError(msg);
        setReport(null);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [queryParams]);

  // -------- PDF download --------
  const [downloading, setDownloading] = useState(false);

  const handleDownloadPdf = async () => {
    if (!queryParams.projectId || !queryParams.from || !queryParams.to) return;

    const container = document.getElementById("labor-report-doc");
    if (!container) {
      console.error("Labor report container not found");
      return;
    }

    try {
      setDownloading(true);

      const filename = `labor-report-${queryParams.from}-to-${queryParams.to}.pdf`;

      const opt = {
        margin: [10, 10, 10, 10],
        filename,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
        },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["css", "legacy"] },
      };

      await html2pdf().set(opt).from(container).save();
    } catch (e) {
      console.error("Labor PDF download failed", e);
    } finally {
      setDownloading(false);
    }
  };

  // -------- navigation back to Labor tab --------
  const handleBackToLabor = () => {
    if (location.state?.returnTo) {
      navigate(location.state.returnTo);
      return;
    }
    navigate(-1);
  };

  // -------- filters modal state --------
  const [showFilters, setShowFilters] = useState(false);
  const [tmpFrom, setTmpFrom] = useState(queryParams.from || "");
  const [tmpTo, setTmpTo] = useState(queryParams.to || "");
  const [tmpDetail, setTmpDetail] = useState(queryParams.detail || "summary");

  useEffect(() => {
    if (!showFilters) return;
    setTmpFrom(queryParams.from || "");
    setTmpTo(queryParams.to || "");
    setTmpDetail(queryParams.detail || "summary");
  }, [showFilters, queryParams]);

  const handleApplyFilters = () => {
    const next = new URLSearchParams(location.search);

    next.set("from", tmpFrom);
    next.set("to", tmpTo);
    next.set("detail", tmpDetail);
    next.set("type", "labor");

    setLoading(true);
    setError(null);

    navigate({ search: next.toString() }, { replace: false });

    setShowFilters(false);
  };

  // -------- loading / error --------
  if (loading) {
    return (
      <div className="report-print-root">
        <div className="report-preview-scroll">
          <div className="report-print-shell">
            <div className="report-loading">Preparing labor report…</div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="report-print-root">
        <div className="report-preview-scroll">
          <div className="report-print-shell">
            <div className="report-error">
              <h2>Unable to generate labor report</h2>
              <p>{error || "No data available for the selected filters."}</p>
              <button
                className="report-filter-btn"
                onClick={() => setShowFilters(true)}
              >
                Edit Filters
              </button>
              <button
                className="report-back-btn"
                style={{ marginTop: 8 }}
                onClick={handleBackToLabor}
              >
                <FaChevronLeft size={14} />
              </button>
            </div>
          </div>
        </div>

        <LaborReportModal
          show={showFilters}
          onClose={() => setShowFilters(false)}
          startDate={tmpFrom}
          endDate={tmpTo}
          setStartDate={setTmpFrom}
          setEndDate={setTmpTo}
          format={tmpDetail}
          setFormat={setTmpDetail}
          mode="filters"
          onApply={handleApplyFilters}
          applyLabel="Apply Filters"
        />
      </div>
    );
  }

  // -------- unpack report JSON --------
  const { meta = {}, summary = {}, tables = [] } = report;

  const dailyTable =
    tables.find((t) => /daily/i.test(t.title || "")) || tables[0] || null;

  const workerTable =
    tables.find((t) => /worker summary/i.test(t.title || "")) ||
    tables[1] ||
    tables[0] ||
    null;

  const topOutstandingTable =
    tables.find((t) => /top workers by outstanding/i.test(t.title || "")) ||
    tables[2] ||
    null;

  const isDetailed = queryParams.detail === "detailed";

  return (
    <div className="report-print-root">
      {/* top toolbar */}
      <div className="report-toolbar">
        <div className="report-toolbar-left">
          <button
            className="report-back-btn"
            onClick={handleBackToLabor}
            aria-label="Back to Labor"
          >
            <FaChevronLeft size={14} />
          </button>

          <div className="report-toolbar-title">
            <h2>Labor Report Preview</h2>
            <span className="report-toolbar-period">
              {queryParams.from} → {queryParams.to}
            </span>
          </div>
        </div>

        <div className="report-toolbar-right">
          <button
            className="report-filter-btn"
            onClick={() => setShowFilters(true)}
          >
            Edit Filters
          </button>

          <button
            className="report-download-btn"
            onClick={handleDownloadPdf}
            disabled={downloading}
          >
            {downloading ? "Generating PDF…" : "Download PDF"}
          </button>
        </div>
      </div>

      {/* screen watermark */}
      <div className="report-watermark">Site360</div>

      {/* scrollable PDF region */}
      <div className="report-preview-scroll">
        <div id="labor-report-doc" className="report-print-shell">
          {/* watermark inside PDF */}
          <img
            src={site360Logo}
            alt=""
            className="report-doc-watermark"
            crossOrigin="anonymous"
          />

          {/* HEADER */}
          <header className="report-header">
            <div className="report-header-left">
              <div className="report-logo">Site360</div>
              <div className="report-title-block">
                <h1>Labor Report</h1>
                {meta.projectName && (
                  <p className="report-project">{meta.projectName}</p>
                )}
              </div>
            </div>

            <div className="report-header-right">
              <div className="report-meta-row">
                <span className="label">Type</span>
                <span className="value">Labor</span>
              </div>

              <div className="report-meta-row">
                <span className="label">Period</span>
                <span className="value">
                  {(meta.from || queryParams.from || "—")} →{" "}
                  {(meta.to || queryParams.to || "—")}
                </span>
              </div>

              <div className="report-meta-row">
                <span className="label">Detail</span>
                <span className="value">
                  {isDetailed ? "Detailed" : "Summary"}
                </span>
              </div>

              <div className="report-meta-row">
                <span className="label">Generated</span>
                <span className="value">{formatDateTime(meta.generatedAt)}</span>
              </div>
            </div>
          </header>

          {/* SUMMARY */}
          <section className="report-section">
            <div className="section-header">
              <h2>Summary</h2>
              <p>High-level snapshot of attendance and labor payments.</p>
            </div>

            <div className="metric-grid">
              <div className="metric-card">
                <div className="metric-label">Total Workers</div>
                <div className="metric-value">
                  {summary.totalWorkers ?? 0}
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-label">Present Days</div>
                <div className="metric-value">
                  {summary.totalPresentDays ?? 0}
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-label">Absent Days</div>
                <div className="metric-value">
                  {summary.totalAbsentDays ?? 0}
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-label">Leave Days</div>
                <div className="metric-value">
                  {summary.totalLeaveDays ?? 0}
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-label">Total Payments (count)</div>
                <div className="metric-value">
                  {summary.totalPayments ?? 0}
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-label">Total Paid Amount</div>
                <div className="metric-value">
                  {formatCurrency(summary.totalPaidAmount)}
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-label">Outstanding (Period)</div>
                <div className="metric-value">
                  {formatCurrency(summary.totalOutstanding)}
                </div>
              </div>
            </div>
          </section>

          {/* DAILY ATTENDANCE & PAYMENTS TABLE */}
          {dailyTable && (
            <section className="report-section">
              <div className="section-header">
                <h3>
                  {dailyTable.title || "Daily Attendance & Payments Summary"}
                </h3>
              </div>

              <table className="report-table">
                <thead>
                  <tr>
                    {dailyTable.columns.map((col) => (
                      <th key={col}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dailyTable.rows.map((row, idx) => (
                    <tr key={idx}>
                      {row.map((cell, i) => (
                        <td
                          key={i}
                          className={i === row.length - 1 ? "right" : ""}
                        >
                          {i === 0 ? formatDate(cell) : cell ?? "-"}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {/* WORKER SUMMARY TABLE */}
          {workerTable && (
            <section className="report-section">
              <div className="section-header">
                <h3>{workerTable.title || "Worker Summary"}</h3>
                <p>
                  Per-worker attendance and payment overview for the selected
                  period.
                </p>
              </div>

              <table className="report-table">
                <thead>
                  <tr>
                    {workerTable.columns.map((col) => (
                      <th key={col}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {workerTable.rows.map((row, idx) => (
                    <tr key={idx}>
                      {row.map((cell, i) => {
                        const colName = workerTable.columns[i] || "";
                        const rightAlign = /wage|paid|earned|outstanding|amount/i.test(
                          colName
                        );
                        return (
                          <td key={i} className={rightAlign ? "right" : ""}>
                            {cell ?? "-"}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {/* TOP WORKERS BY OUTSTANDING – only in detailed mode */}
          {isDetailed && topOutstandingTable && (
            <section className="report-section">
              <div className="section-header">
                <h3>
                  {topOutstandingTable.title || "Top Workers by Outstanding"}
                </h3>
                <p>Workers with the highest outstanding in the period.</p>
              </div>

              <table className="report-table">
                <thead>
                  <tr>
                    {topOutstandingTable.columns.map((col) => (
                      <th key={col}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {topOutstandingTable.rows.map((row, idx) => (
                    <tr key={idx}>
                      {row.map((cell, i) => {
                        const colName = topOutstandingTable.columns[i] || "";
                        const rightAlign = /wage|paid|earned|outstanding|amount/i.test(
                          colName
                        );
                        return (
                          <td key={i} className={rightAlign ? "right" : ""}>
                            {cell ?? "-"}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {/* FOOTER */}
          <footer className="report-footer">
            <div className="footer-brand">Site360</div>
            <div className="footer-text">
              Construction Management Platform · https://site360.app
            </div>
          </footer>
        </div>
      </div>

      {/* bottom sticky bar */}
      <div className="report-bottom-sticky">
        <div className="bottom-meta">
          <div className="title">Labor Report</div>
          <div className="period">
            {queryParams.from} → {queryParams.to}
          </div>
        </div>

        <button
          className="report-download-btn"
          onClick={handleDownloadPdf}
          disabled={downloading}
        >
          {downloading ? "Generating PDF…" : "Download PDF"}
        </button>
      </div>

      {/* Filters modal */}
      <LaborReportModal
        show={showFilters}
        onClose={() => setShowFilters(false)}
        startDate={tmpFrom}
        endDate={tmpTo}
        setStartDate={setTmpFrom}
        setEndDate={setTmpTo}
        format={tmpDetail}
        setFormat={setTmpDetail}
        mode="filters"
        onApply={handleApplyFilters}
        applyLabel="Apply Filters"
      />
    </div>
  );
};

export default LaborReportPreview;
